// LogDeEnvio.tsx
//
// Sub-tela "Log de envio" — e agora ela TEM o que mostrar.
//
// O que estava escrito aqui antes, em texto que o dono lia: "a entidade
// MensagemSaida não existe no schema; não há tabela, não há rota e não há
// nenhuma linha gravada em lugar nenhum". Era verdade. A compra automática
// (§10.5) criou a tabela `asup."MensagemSaida"` e a rota
// `GET /suprimentos/compras/mensagens`, e é isso que esta tela lê.
//
// O QUE ELA MOSTRA, e é o critério do card: destinatário, canal, template,
// **texto exato**, hora e estado. O texto exato inteiro, sem cortar — o valor
// desta tela é justamente poder ler, palavra por palavra, o que sairia antes de
// qualquer coisa sair.
//
// A DISTINÇÃO QUE NÃO PODE SUMIR: `simulada` NÃO é "enviada". Enquanto o
// adaptador de canal for o de simulação, toda linha nasce `simulada` e nada
// chegou a fornecedor nenhum. Um dono que acha que a mensagem saiu não liga para
// o fornecedor — por isso o destaque é explícito e o contador de mensagens
// realmente enviadas aparece no topo, vindo do servidor.
//
// O que esta tela continua NÃO fazendo: não inventa linha de exemplo, não tem
// botão de teste e não tem botão de reenvio. Reenviar é ação externa.

import React, { useCallback, useEffect, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/comunicacoesSection/index";
import comprasService, {
  MensagemDeSaida,
} from "../../../../services/compras.service";

function comoDataHora(valor: string | null): string {
  if (!valor) return "—";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "—";
  return data.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

const LogDeEnvio: React.FC = () => {
  const [mensagens, definirMensagens] = useState<MensagemDeSaida[]>([]);
  const [enviadasParaFornecedorReal, definirEnviadas] = useState<number | null>(
    null,
  );
  const [carregando, definirCarregando] = useState(true);
  const [erro, definirErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    definirCarregando(true);
    definirErro(null);
    try {
      const resposta = await comprasService.mensagens({ limit: 100 });
      definirMensagens(resposta.dados);
      definirEnviadas(resposta.enviadasParaFornecedorReal ?? null);
    } catch (e: any) {
      definirErro(
        e?.response?.data?.message ??
          "Não foi possível ler o log de envio. Se o servidor ainda não tem as " +
            "tabelas de compras, elas nascem no próximo boot.",
      );
    } finally {
      definirCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return (
    <AlfredoStyles.FieldsContainer>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              receipt_long
            </AlfredoStyles.MaterialIcons>
            Log de envio
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            Toda mensagem vira registro: para quem, por qual canal, com qual
            template, o texto exato e quando
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      {enviadasParaFornecedorReal !== null && (
        <AlfredoStyles.AvisoTopo
          $tom={enviadasParaFornecedorReal === 0 ? "ok" : "atencao"}
        >
          <AlfredoStyles.MaterialIcons>
            {enviadasParaFornecedorReal === 0 ? "verified" : "warning"}
          </AlfredoStyles.MaterialIcons>
          {enviadasParaFornecedorReal === 0 ? (
            <span>
              <strong>Nada saiu para fornecedor real.</strong> O canal está em modo
              simulação: o texto do pedido é montado e gravado aqui, e nenhum
              WhatsApp, e-mail ou SMS é disparado. Mensagens com estado{" "}
              <em>enviada</em> para contato de fornecedor: 0.
            </span>
          ) : (
            <span>
              <strong>
                {enviadasParaFornecedorReal} mensagem(ns) saíram para fornecedor
                real.
              </strong>{" "}
              O envio real está ligado no servidor.
            </span>
          )}
        </AlfredoStyles.AvisoTopo>
      )}

      {erro && (
        <AlfredoStyles.Aviso $tom="erro">
          <AlfredoStyles.MaterialIcons>error</AlfredoStyles.MaterialIcons>
          {erro}
        </AlfredoStyles.Aviso>
      )}

      {carregando && (
        <AlfredoStyles.LoadingContainer>
          <AlfredoStyles.LoadingSpinner />
        </AlfredoStyles.LoadingContainer>
      )}

      {!carregando && !erro && mensagens.length === 0 && (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>outbox</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            Nenhuma mensagem registrada ainda
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            A tabela existe e a rota responde — esta lista está vazia porque
            nenhuma ordem de compra chegou ao ponto de ter o texto montado. Isso
            acontece depois que você aprova uma ordem em Suprimentos → Compras →
            Ordens e a janela de arrependimento de 15 minutos vence.
          </AlfredoStyles.EmptyStateDescription>
        </AlfredoStyles.EmptyState>
      )}

      {mensagens.length > 0 && (
        <AlfredoStyles.Lista>
          {mensagens.map((mensagem) => (
            <AlfredoStyles.ListaItem key={mensagem.mensagemUuid}>
              <AlfredoStyles.ListaItemCorpo>
                <AlfredoStyles.ListaItemTitulo>
                  {mensagem.codigoDaOrdem ?? "(sem ordem)"} · {mensagem.proposito}
                  <AlfredoStyles.Etiqueta
                    $tom={mensagem.estado === "enviada" ? "gravado" : "semente"}
                  >
                    {mensagem.estado}
                  </AlfredoStyles.Etiqueta>
                  <AlfredoStyles.Etiqueta $tom="neutro">
                    {mensagem.canal}
                  </AlfredoStyles.Etiqueta>
                </AlfredoStyles.ListaItemTitulo>

                <AlfredoStyles.ListaItemTexto>
                  Para <strong>{mensagem.destinatario}</strong> (
                  {mensagem.destinatarioTipo}) · template{" "}
                  <strong>{mensagem.template}</strong> ·{" "}
                  {comoDataHora(mensagem.criadaEm)}
                  {mensagem.enviadaEm
                    ? ` · saiu em ${comoDataHora(mensagem.enviadaEm)}`
                    : ""}
                </AlfredoStyles.ListaItemTexto>

                {mensagem.estado === "simulada" && (
                  <AlfredoStyles.ListaItemTexto>
                    <strong>Nada saiu para o fornecedor.</strong> Este é o texto
                    exato que sairia se o envio real estivesse ligado.
                  </AlfredoStyles.ListaItemTexto>
                )}

                {mensagem.erro && (
                  <AlfredoStyles.ListaItemTexto>
                    Erro do provedor: {mensagem.erro}
                  </AlfredoStyles.ListaItemTexto>
                )}

                <AlfredoStyles.Trecho>{mensagem.textoExato}</AlfredoStyles.Trecho>
              </AlfredoStyles.ListaItemCorpo>
            </AlfredoStyles.ListaItem>
          ))}
        </AlfredoStyles.Lista>
      )}
    </AlfredoStyles.FieldsContainer>
  );
};

export default LogDeEnvio;
